// frontend/src/services/api.js - COMPLETE LOCALHOST VERSION
import axios from 'axios';

const resolveApiBaseUrl = () => {
  let url = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Prevent Mixed Content: HTTPS page must not call HTTP API
  if (typeof window !== 'undefined' && window.location?.protocol === 'https:' && url.startsWith('http://')) {
    url = url.replace(/^http:\/\//i, 'https://');
  }

  return url;
};

const API_BASE_URL = resolveApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

const canUseBranchFilter = () => {
  const role = String(localStorage.getItem('erp_user_role') || '')
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, '_');
  return role === 'super_admin' || role === 'admin' || role === 'super_accountant';
};

const shouldSkipBranchFilter = (url = '') => {
  const path = String(url).split('?')[0];
  // Don't scope branch master data / auth
  return (
    path === '/branches' ||
    path === '/branches/active' ||
    path.endsWith('/branches') ||
    path.endsWith('/branches/active') ||
    path.includes('/auth') ||
    path.includes('/login') ||
    path.includes('/device-types') ||
    path.includes('/device-brands')
  );
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('erp_auth_token') || 'demo-admin-token';

    // Always send Authorization so backend can resolve the real user role
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Admin branch dropdown = VIEW FILTER only (does not shift/move records)
    const filterId = localStorage.getItem('erp_admin_branch_filter');
    if (
      canUseBranchFilter() &&
      filterId &&
      !config.skipBranchFilter &&
      !shouldSkipBranchFilter(config.url)
    ) {
      config.params = { ...(config.params || {}), branch_id: filterId };

      const method = String(config.method || 'get').toLowerCase();
      if (['post', 'put', 'patch'].includes(method) && config.data && typeof config.data === 'object') {
        if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
          if (!config.data.has('branch_id')) {
            config.data.append('branch_id', filterId);
          }
        } else if (config.data.branch_id == null || config.data.branch_id === '') {
          config.data = { ...config.data, branch_id: parseInt(filterId, 10) };
        }
      }
    }

    // Logging for debugging
    if (process.env.NODE_ENV !== 'production') {
      console.log(`📤 API Request: ${config.method.toUpperCase()} ${config.url}`, config.data || '');
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Add logging for debugging
    if (process.env.NODE_ENV !== 'production') {
      console.log(`📥 API Response: ${response.config.method.toUpperCase()} ${response.config.url}`, response.data);
    }
    return response.data;
  },
  (error) => {
    console.error('❌ API Error:', error.config?.url, error.response?.status);
    
    if (error.response) {
      const errorData = {
        success: false,
        message: error.response.data?.message || `Server error: ${error.response.status}`,
        error: error.response.data?.error || error.response.statusText,
        status: error.response.status,
        timestamp: new Date().toISOString()
      };
      
      if (error.response.status === 401) {
        localStorage.removeItem('erp_auth_token');
        localStorage.removeItem('erp_user');
        localStorage.removeItem('erp_username');
        localStorage.removeItem('erp_user_role');
        localStorage.removeItem('erp_user_email');
        localStorage.removeItem('erp_user_id');
        window.location.href = '/login';
        errorData.message = 'Session expired. Please login again.';
      }
      
      if (error.response.status === 404) {
        errorData.message = 'Resource not found. Please check the URL.';
      }

      // Preserve structured validation fields (e.g. SSI missing_labels)
      if (error.response.data?.missing_labels) {
        errorData.missing_labels = error.response.data.missing_labels;
      }
      if (Array.isArray(error.response.data?.missing_labels) && error.response.data.missing_labels.length) {
        errorData.message = `Mark Good/Bad for: ${error.response.data.missing_labels.join(', ')}`;
      }
      
      return Promise.reject(errorData);
    } else if (error.request) {
      return Promise.reject({
        success: false,
        message: 'Network error. Please check your connection.',
        error: 'No response from server',
        timestamp: new Date().toISOString()
      });
    } else {
      return Promise.reject({
        success: false,
        message: 'Request failed. Please try again.',
        error: error.message,
        timestamp: new Date().toISOString()
      });
    }
  }
);

// ==================== TEST & DEBUG API ====================
export const testConnection = () => api.get('/test');
export const testPOSConnection = () => api.get('/pos/test');
export const debugAPI = (data) => api.post('/pos-debug', data);

// ==================== DASHBOARD API ====================
export const getDashboardStats = () => api.get('/dashboard-stats');

// ==================== POS API ====================
export const getPOS = (query = '') => api.get(`/pos${query}`);
export const filterInvoices = (query = '') => api.get(`/invoices/filter${query}`);
export const getPOSById = (id) => api.get(`/pos/${id}`);

// Create POS with proper data structure
export const createPOS = (posData) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('📤 Sending POS data to backend:', JSON.stringify(posData, null, 2));
  }
  return api.post('/pos', posData);
};

export const refundPOS = (id, refundData) => api.post(`/pos/${id}/refund`, refundData);
export const voidPOS = (id, reason) => api.put(`/pos/${id}/void`, { reason });
export const getPOSStats = () => api.get('/pos/stats');
export const searchSerialNumbers = (query) => api.get(`/pos/search/serial${query}`);
export const getWarrantyInfo = (query) => api.get(`/repairs/warranty-pos-lookup${query}`);

// POS STATUS UPDATE - Handles both string and object
export const updatePOSStatus = (id, statusData) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log(`📤 Updating POS status for ${id}:`, statusData);
  }
  
  // If statusData is a string, send as { status: statusData }
  if (typeof statusData === 'string') {
    return api.put(`/pos/${id}/status`, { status: statusData });
  }
  
  // If it's an object, send as-is
  return api.put(`/pos/${id}/status`, statusData);
};

// ==================== PRODUCTS API ====================
export const getProducts = (params = {}) => api.get('/products', { params });

// Issue Sun System Office
export const getOfficeIssues = (params = {}) => api.get('/office-issues', { params });
export const getOfficeIssueById = (id) => api.get(`/office-issues/${id}`);
export const createOfficeIssue = (data) => api.post('/office-issues', data);
export const getProductById = (id) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('📤 Fetching product by ID:', id);
  }
  return api.get(`/products/${id}`);
};
export const createProduct = (productData) => api.post('/products', productData);
export const updateProduct = (id, productData) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('📤 Updating product:', id, productData);
  }
  return api.put(`/products/${id}`, productData);
};
export const deleteProduct = (id) => api.delete(`/products/${id}`);
export const getProductByBarcode = (barcode) => api.get(`/products/barcode/${barcode}`);
export const searchProducts = (query) => api.get(`/products/search${query}`);
export const getLowStockProducts = () => api.get('/products/low-stock');
export const getProductCategories = () => api.get('/products/categories');
export const getProductStats = () => api.get('/products/stats');
export const bulkUpdateProducts = (data) => api.put('/products/bulk', data);
export const updateProductStock = (id, data) => api.put(`/products/${id}/stock`, data);
export const toggleProductStatus = (id) => api.put(`/products/${id}/toggle-status`);

// ==================== GRN API ====================
export const getGRNList = (query = '') => api.get(`/grn${query}`);
export const getGRNById = (id) => api.get(`/grn/${id}`);
export const createGRN = (data) => api.post('/grn', data);
export const updatePendingGRN = (id, data) => api.put(`/grn/${id}/pending`, data);
export const approveGRN = (id) => api.post(`/grn/${id}/approve`);
export const getGRNByProduct = (productId, query = '') => api.get(`/grn/product/${productId}${query}`);
export const getGRNBySerial = (serialNumber) => api.get(`/grn/serial/${encodeURIComponent(serialNumber)}`);
export const getGRNSuppliers = () => api.get('/grn/suppliers');

// ==================== PRODUCT SERIALS API ====================
/**
 * Get available serials for a product
 * @param {number} productId - Product ID
 * @param {Object} params - Query parameters (status, limit, etc.)
 * @returns {Promise}
 */
export const getProductSerials = (productId, params = {}) => {
  const queryString = buildQueryString(params);
  if (process.env.NODE_ENV !== 'production') {
    console.log(`📤 Fetching serials for product ${productId} with params:`, params);
  }
  return api.get(`/products/${productId}/serials${queryString}`);
};

/**
 * Get all serials for a product
 * @param {number} productId - Product ID
 * @param {Object} params - Query parameters
 * @returns {Promise}
 */
export const getAllProductSerials = (productId, params = {}) => {
  const queryString = buildQueryString(params);
  return api.get(`/products/${productId}/all-serials${queryString}`);
};

/**
 * Add a new serial to a product
 * @param {number} productId - Product ID
 * @param {Object} serialData - Serial data
 * @returns {Promise}
 */
export const addProductSerial = (productId, serialData) => {
  return api.post(`/products/${productId}/serials`, serialData);
};

/**
 * Update serial status
 * @param {number} serialId - Serial ID
 * @param {Object} statusData - Status update data
 * @returns {Promise}
 */
export const updateSerialStatus = (serialId, statusData) => {
  return api.put(`/products/serials/${serialId}/status`, statusData);
};

/**
 * Update serial details
 * @param {number} productId - Product ID
 * @param {number} serialId - Serial ID
 * @param {Object} serialData - Serial data
 * @returns {Promise}
 */
export const updateProductSerial = (productId, serialId, serialData) => {
  return api.put(`/products/${productId}/serials/${serialId}`, serialData);
};

/**
 * Delete a serial
 * @param {number} productId - Product ID
 * @param {number} serialId - Serial ID
 * @returns {Promise}
 */
export const deleteProductSerial = (productId, serialId) => {
  return api.delete(`/products/${productId}/serials/${serialId}`);
};

/**
 * Reserve serials for sale
 * @param {Array} serialIds - Array of serial IDs to reserve
 * @param {string} posTransactionId - POS transaction ID (optional)
 * @returns {Promise}
 */
export const reserveSerials = (serialIds, posTransactionId = null) => {
  return api.post('/products/serials/reserve', { 
    serial_ids: serialIds,
    pos_transaction_id: posTransactionId 
  });
};

/**
 * Search serials by number
 * @param {string} query - Search query
 * @returns {Promise}
 */
export const searchSerials = (query) => {
  return api.get(`/products/serials/search?q=${encodeURIComponent(query)}`);
};

// ==================== PRODUCT ISSUES API ====================
/**
 * Get all product issues with optional filters
 * @param {Object} params - Query parameters (product_id, status, issue_type, severity, start_date, end_date, page, limit)
 * @returns {Promise}
 */
export const getAllProductIssues = (params = {}) => {
  const queryString = buildQueryString(params);
  return api.get(`/product-issues${queryString}`);
};

/**
 * Get issues for a specific product
 * @param {number} productId - Product ID
 * @returns {Promise}
 */
export const getProductIssues = (productId) => {
  return api.get(`/product-issues/product/${productId}`);
};

/**
 * Get single issue by ID
 * @param {number} id - Issue ID
 * @returns {Promise}
 */
export const getProductIssueById = (id) => {
  return api.get(`/product-issues/${id}`);
};

/**
 * Create a new product issue
 * @param {Object} issueData - Issue data
 * @returns {Promise}
 */
export const createProductIssue = (issueData) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('📤 Creating product issue:', issueData);
  }
  return api.post('/product-issues', issueData);
};

/**
 * Update issue details
 * @param {number} id - Issue ID
 * @param {Object} issueData - Issue data
 * @returns {Promise}
 */
export const updateProductIssue = (id, issueData) => {
  return api.put(`/product-issues/${id}`, issueData);
};

/**
 * Update issue status
 * @param {number} id - Issue ID
 * @param {Object} statusData - Status update data
 * @returns {Promise}
 */
export const updateProductIssueStatus = (id, statusData) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log(`📤 Updating issue ${id} status:`, statusData);
  }
  return api.put(`/product-issues/${id}/status`, statusData);
};

/**
 * Delete an issue (only pending issues)
 * @param {number} id - Issue ID
 * @returns {Promise}
 */
export const deleteProductIssue = (id) => {
  return api.delete(`/product-issues/${id}`);
};

/**
 * Resolve an issue
 * @param {number} id - Issue ID
 * @param {Object} resolutionData - Resolution data
 * @returns {Promise}
 */
export const resolveProductIssue = (id, resolutionData) => {
  return api.put(`/product-issues/${id}/resolve`, resolutionData);
};

/**
 * Get product issues statistics
 * @returns {Promise}
 */
export const getProductIssuesStats = () => {
  return api.get('/product-issues/stats');
};

/**
 * Export issues to CSV
 * @param {Object} params - Query parameters
 * @returns {Promise} - Blob response
 */
export const exportProductIssuesToCSV = (params = {}) => {
  const queryString = buildQueryString(params);
  return api.get(`/product-issues/export/csv${queryString}`, {
    responseType: 'blob'
  });
};

/**
 * Export issues to PDF
 * @param {Object} params - Query parameters
 * @returns {Promise} - Blob response
 */
export const exportProductIssuesToPDF = (params = {}) => {
  const queryString = buildQueryString(params);
  return api.get(`/product-issues/export/pdf${queryString}`, {
    responseType: 'blob'
  });
};

/**
 * Export product summary (Good vs Issue) to CSV
 * @returns {Promise} - Blob response
 */
export const exportProductSummary = () => {
  return api.get('/products/export/summary', {
    responseType: 'blob'
  });
};

/**
 * Export issues report (alias for exportProductIssuesToCSV)
 * @param {Object} params - Query parameters
 * @returns {Promise} - Blob response
 */
export const exportIssuesReport = (params = {}) => {
  const queryString = buildQueryString(params);
  return api.get(`/products/export/issues${queryString}`, {
    responseType: 'blob'
  });
};

// ==================== CUSTOMERS API ====================
export const getCustomers = () => api.get('/customers');
export const getCustomerById = (id) => api.get(`/customers/${id}`);
export const createCustomer = (customerData) => api.post('/customers', customerData);
export const updateCustomer = (id, customerData) => api.put(`/customers/${id}`, customerData);
export const deleteCustomer = (id) => api.delete(`/customers/${id}`);
export const searchCustomers = (query) => api.get(`/customers/search${query}`);
export const getCustomerStats = () => api.get('/customers/stats');
export const getActiveCustomers = () => api.get('/customers/active');
export const getCreditCustomers = () => api.get('/customers/credit');

// ==================== INVOICES API ====================
export const getInvoices = (query = '') => api.get(`/invoices${query}`);
export const getInvoiceById = (id) => api.get(`/invoices/${id}`);
export const createInvoice = (invoiceData) => api.post('/invoices', invoiceData);
export const updateInvoice = (id, invoiceData) => api.put(`/invoices/${id}`, invoiceData);
export const deleteInvoice = (id) => api.delete(`/invoices/${id}`);
export const updateInvoiceStatus = (id, statusData) => api.put(`/invoices/${id}/status`, statusData);
export const markAsPaid = (id) => api.put(`/invoices/${id}/paid`);
export const getInvoiceStats = () => api.get('/invoices/stats');
export const getOverdueInvoices = () => api.get('/invoices/overdue');
export const sendInvoice = (id) => api.put(`/invoices/${id}/send`);
export const printInvoice = (id) => api.get(`/invoices/${id}/print`);
export const downloadInvoice = (id) => api.get(`/invoices/${id}/download`, { responseType: 'blob' });
export const sendInvoiceEmail = (id, emailData) => api.post(`/invoices/${id}/send-email`, emailData);

// ==================== REPAIR INVOICE API ====================
export const getRepairs = (query = '') => api.get(`/repairs${query}`);
export const getRepairById = (id) => api.get(`/repairs/${id}`);
export const createRepair = (repairData) => api.post('/repairs', repairData);
export const updateRepair = (id, repairData) => api.put(`/repairs/${id}`, repairData);
export const deleteRepair = (id) => api.delete(`/repairs/${id}`);
export const updateDiagnosis = (id, diagnosisData) => api.put(`/repairs/${id}/diagnosis`, diagnosisData);
export const createRepairQuotation = (id, quotationData) => api.post(`/repairs/${id}/quotation`, quotationData);
export const updateRepairQuotation = (id, quotationData) => api.put(`/repairs/${id}/quotation`, quotationData);
export const acceptQuotation = (id, data) => api.post(`/repairs/${id}/accept`, data);
export const rejectQuotation = (id, data) => api.post(`/repairs/${id}/reject-quotation`, data);
export const addRepairParts = (id, partsData) => api.post(`/repairs/${id}/parts`, partsData);
export const completeRepair = (id, data) => api.put(`/repairs/${id}/complete`, data);
export const recordRepairPayment = (id, paymentData) => api.post(`/repairs/${id}/payment`, paymentData);
export const deliverRepair = (id) => api.put(`/repairs/${id}/deliver`);
export const getRepairStats = () => api.get('/repairs/stats');
export const searchRepairsBySerial = (serial) => api.get(`/repairs/search?serial=${serial}`);
export const getWarrantyRepairs = () => api.get('/repairs/warranty');
export const getRepairCustomers = (params = {}) => {
  const query = buildQueryString(params);
  return api.get(`/repairs/customers${query}`);
};
export const getRepairCustomerProfile = (id) => api.get(`/repairs/customers/${id}`);

// ==================== RENTALS API ====================
export const getRentals = (params = {}) => api.get(`/rentals${buildQueryString(params)}`);
export const getRentalById = (id) => api.get(`/rentals/${id}`);
export const createRental = (data) => api.post('/rentals', data);
export const getRentalStats = () => api.get('/rentals/stats');
export const returnRental = (id, data = {}) => api.post(`/rentals/${id}/return`, data);
export const cancelRental = (id) => api.post(`/rentals/${id}/cancel`);
export const extendRental = (id, data = {}) =>
  api.post(`/rentals/${id}/extend`, typeof data === 'object' && data != null && !Array.isArray(data)
    ? data
    : { extra_periods: data });
export const getRentalPendingInvoices = (params = {}) =>
  api.get(`/rentals/pending-invoices${buildQueryString(params)}`);
export const payRentalInvoice = (id, data) => api.post(`/rentals/invoices/${id}/pay`, data);
export const trackRentalSerial = (serial) =>
  api.get(`/rentals/track${buildQueryString({ serial })}`);
export const getRentalCustomers = (params = {}) =>
  api.get(`/rentals/customers${buildQueryString(params)}`);
export const getRentalCustomerProfile = (id) => api.get(`/rentals/customers/${id}`);
export const getRentalInvoices = (params = {}) =>
  api.get(`/rentals/invoices${buildQueryString(params)}`);
export const getRentalInvoiceById = (id) => api.get(`/rentals/invoices/${id}`);
export const getRentalAgreementTemplate = () => api.get('/rentals/agreement-template');
export const uploadRentalAgreementTemplate = (data) =>
  api.post('/rentals/agreement-template', data, { timeout: 60000 });
export const uploadRentalAgreement = (id, data) =>
  api.post(`/rentals/${id}/agreement`, data, { timeout: 60000 });
export const getRentalDashboard = () => api.get('/rentals/dashboard');
export const getRentalReport = (params = {}) =>
  api.get(`/rentals/report${buildQueryString(params)}`);
export const getOutsideRentals = (params = {}) =>
  api.get(`/rentals/outside${buildQueryString(params)}`);
export const getOutsideRentalProfile = (serial) =>
  api.get(`/rentals/outside/${encodeURIComponent(serial)}`);

// Advanced repair / service workflow
export const getWorkshopQueue = (query = '') => api.get(`/repairs/workshop/queue${query}`);
export const assignTechnician = (id, data) => api.post(`/repairs/${id}/assign`, data);
export const createServiceRequest = (data) => api.post('/repairs/service-request', data);
export const getServiceRequest = (id) => api.get(`/repairs/service-request/${id}`);
export const updateRepairStatus = (id, status, note = '') =>
  api.patch(`/repairs/${id}/status`, typeof status === 'object' ? status : { status, note });
export const updateRepairType = (id, data) => api.patch(`/repairs/${id}/repair-type`, data);

// Outsource technician master list
export const getOutsourceTechnicians = (params = {}) => {
  const query = buildQueryString(params);
  return api.get(`/repairs/outsource-technicians${query}`);
};
export const createOutsourceTechnician = (data) => api.post('/repairs/outsource-technicians', data);
export const updateOutsourceTechnician = (id, data) =>
  api.put(`/repairs/outsource-technicians/${id}`, data);
export const deleteOutsourceTechnician = (id) =>
  api.delete(`/repairs/outsource-technicians/${id}`);

// Common fault descriptions (diagnosis)
export const getRepairCommonFaults = (params = {}) => {
  const query = buildQueryString(params);
  return api.get(`/repairs/common-faults${query}`);
};
export const createRepairCommonFault = (data) => api.post('/repairs/common-faults', data);
export const updateRepairCommonFault = (id, data) => api.put(`/repairs/common-faults/${id}`, data);
export const deleteRepairCommonFault = (id) => api.delete(`/repairs/common-faults/${id}`);

// In-house technician master list
export const getInhouseTechnicians = (params = {}) => {
  const query = buildQueryString(params);
  return api.get(`/repairs/inhouse-technicians${query}`);
};
export const createInhouseTechnician = (data) => api.post('/repairs/inhouse-technicians', data);
export const updateInhouseTechnician = (id, data) =>
  api.put(`/repairs/inhouse-technicians/${id}`, data);
export const deleteInhouseTechnician = (id) =>
  api.delete(`/repairs/inhouse-technicians/${id}`);

// Workshop diagnosis / collection workflow
export const submitRepairDiagnosis = (id, data) => api.post(`/repairs/${id}/diagnosis/submit`, data);
export const saveRepairOtherPayments = (id, data) => api.patch(`/repairs/${id}/other-payments`, data);
export const approveRepairDiagnosis = (id, data) => api.post(`/repairs/${id}/diagnosis/approve`, data);
export const rejectRepairDiagnosis = (id, data) => api.post(`/repairs/${id}/diagnosis/reject`, data);
export const getDiagnosisParts = (id) => api.get(`/repairs/${id}/diagnosis-parts`);
export const markPartsReceived = (id, data) => api.patch(`/repairs/${id}/parts-received`, data);
export const completeRepairCollection = (id, data) => api.post(`/repairs/${id}/complete-collection`, data);
export const markRepairCollected = (id) => api.post(`/repairs/${id}/mark-collected`);
export const getReadyToCollect = () => api.get('/repairs/ready-to-collect');
export const getRepairDoneList = () => api.get('/repairs/repair-done');

// Outsource pathway + QA
export const updateOutsourceWorkflow = (id, data) =>
  api.patch(`/repairs/${id}/outsource-workflow`, data);
export const setOutsourceRepairPrice = (id, data) =>
  api.post(`/repairs/${id}/outsource-price`, data);
export const assignOutsourceTechnician = (id, data) =>
  api.post(`/repairs/${id}/outsource-assign`, data);
export const informOutsourceTechOngoing = (id, data = {}) =>
  api.post(`/repairs/${id}/outsource-inform-ongoing`, data);
export const informOutsourcePartialFix = (id, data) =>
  api.post(`/repairs/${id}/outsource-partial-fix`, data);
export const markOutsourceNeedCollect = (id, data = {}) =>
  api.post(`/repairs/${id}/outsource-need-collect`, data);
export const markOutsourceReceivedQa = (id, data = {}) =>
  api.post(`/repairs/${id}/outsource-received-qa`, data);
export const rejectRepairQuotation = (id, data = {}) =>
  api.post(`/repairs/${id}/quotation/reject`, data);
export const getQaQueue = () => api.get('/repairs/qa').catch((err) => {
  // Fallback alias if Contabo has older partial deploy
  if (err?.response?.status === 404) return api.get('/repairs/qa-queue');
  throw err;
});
export const submitQaCheck = (id, data) => api.post(`/repairs/${id}/qa`, data);

export const updateWorkflowStep = (id, stepId, data) =>
  api.patch(`/repairs/${id}/workflow/${stepId}`, data);
export const addRepairNote = (id, note) =>
  api.post(`/repairs/${id}/notes`, typeof note === 'string' ? { note } : note);
export const addRepairAttachment = (id, data) => api.post(`/repairs/${id}/attachments`, data);
export const createPartRequest = (data) => api.post('/repairs/part-requests', data);
export const getPartRequests = (query = '') => api.get(`/repairs/part-requests${query}`);
export const approvePartRequest = (id, data) => api.post(`/repairs/part-requests/${id}/approve`, data);
export const rejectPartRequest = (id, data) => api.post(`/repairs/part-requests/${id}/reject`, data);
export const deletePartRequest = (id) => api.delete(`/repairs/part-requests/${id}`);
export const generateRepairInvoice = (id) => api.post(`/repairs/${id}/generate-invoice`);
export const getRepairInvoice = (id) => api.get(`/repairs/${id}/invoice`);
export const updateRepairInvoice = (id, data) => api.put(`/repairs/${id}/invoice`, data);
export const getRepairInvoices = (query = '') => api.get(`/repairs/invoices/all${query}`);

// Repair management report (admin / super admin)
export const getRepairManagementReport = (params = {}) => {
  const query = buildQueryString(params);
  return api.get(`/repairs/management-report${query}`);
};
export const getRepairReportExpenses = (params = {}) => {
  const query = buildQueryString(params);
  return api.get(`/repairs/management-report/expenses${query}`);
};
export const createRepairReportExpense = (data) => api.post('/repairs/management-report/expenses', data);
export const updateRepairReportExpense = (id, data) =>
  api.put(`/repairs/management-report/expenses/${id}`, data);
export const deleteRepairReportExpense = (id) =>
  api.delete(`/repairs/management-report/expenses/${id}`);

// Outsource repairs overview + technician dashboard
export const getOutsourceRepairs = (params = {}) => {
  const query = buildQueryString(params);
  return api.get(`/repairs/outsource${query}`);
};
export const getOutsourceTechnicianDashboard = (name, params = {}) => {
  const query = buildQueryString(params);
  return api.get(`/repairs/outsource/technician/${encodeURIComponent(name)}${query}`);
};
export const updateOutsourcePayment = (id, data) =>
  api.put(`/repairs/${id}/outsource-payment`, data);

export const getRepairQuotations = (id) => api.get(`/repairs/${id}/quotations`);
export const getRepairQuotation = (id, params = {}) => {
  const query = buildQueryString(params);
  return api.get(`/repairs/${id}/quotation${query}`);
};
export const setActiveRepairQuotation = (id, quotationId) =>
  api.put(`/repairs/${id}/quotation/active`, { quotation_id: quotationId });
export const sendRepairSms = (id, data) => api.post(`/repairs/${id}/sms`, data);
export const shiftRepairBranch = (id, data) => api.patch(`/repairs/${id}/branch`, data);

// SSI Technical
export const getSsiTechnicalMeta = () => api.get('/repairs/ssi-technical/meta');
export const getSsiTechnicalJobs = (params = {}) => {
  const query = buildQueryString(params);
  return api.get(`/repairs/ssi-technical${query}`);
};
export const getSsiTechnicalJob = (id) => api.get(`/repairs/ssi-technical/${id}`);
export const createSsiTechnicalItem = (data) => api.post('/repairs/ssi-technical', data);
export const createSsiBulkGrn = (data) => api.post('/repairs/ssi-technical/bulk-grn', data);
export const saveSsiTechnicalInspection = (id, data) => api.put(`/repairs/ssi-technical/${id}`, data);
export const addSsiPartRequest = (id, data) => api.post(`/repairs/ssi-technical/${id}/part-request`, data);
export const approveSsiTechnicalStock = (id, data = {}) => api.post(`/repairs/ssi-technical/${id}/approve`, data);
export const updateSsiStockProductName = (id, data) => api.put(`/repairs/ssi-technical/${id}/stock-product-name`, data);
export const rejectSsiTechnicalJob = (id, data = {}) => api.post(`/repairs/ssi-technical/${id}/reject`, data);
export const deleteSsiTechnicalJob = (id) => api.delete(`/repairs/ssi-technical/${id}`);

// ==================== BRANCHES API ====================
export const getBranches = () => api.get('/branches', { skipBranchFilter: true });
export const getActiveBranches = () => api.get('/branches/active');
export const createBranch = (data) => api.post('/branches', data);
export const updateBranch = (id, data) => api.put(`/branches/${id}`, data);
export const deleteBranch = (id) => api.delete(`/branches/${id}`);
export const getBranchUsers = (params = {}) => {
  const query = typeof params === 'string' ? params : buildQueryString(params);
  return api.get(`/branches/users/list${query}`);
};
export const createBranchManager = (data) => api.post('/branches/users/manager', data);
export const createBranchStaff = (data) => api.post('/branches/users/staff', data);
export const shiftUserBranch = (userId, data) => api.put(`/branches/users/${userId}/shift`, data);
export const resetBranchUserPassword = (userId, data) =>
  api.put(`/branches/users/${userId}/password`, data);

// ==================== ACCOUNTING API ====================
export const getAccountingDashboard = () => api.get('/accounting/dashboard');
export const getChartOfAccounts = () => api.get('/accounting/accounts');
export const createChartOfAccount = (data) => api.post('/accounting/accounts', data);
export const updateChartOfAccount = (id, data) => api.put(`/accounting/accounts/${id}`, data);
export const deleteChartOfAccount = (id) => api.delete(`/accounting/accounts/${id}`);
export const getJournalEntries = () => api.get('/accounting/journal-entries');
export const createJournalEntry = (data) => api.post('/accounting/journal-entries', data);
export const updateJournalEntryStatus = (id, status) =>
  api.patch(`/accounting/journal-entries/${id}/status`, typeof status === 'object' ? status : { status });
export const getAccountingPeriods = () => api.get('/accounting/periods');
export const createAccountingPeriod = (data) => api.post('/accounting/periods', data);
export const updatePeriodStatus = (id, status) =>
  api.patch(`/accounting/periods/${id}/status`, typeof status === 'object' ? status : { status });
export const deleteAccountingPeriod = (id) => api.delete(`/accounting/periods/${id}`);
export const getCostCenters = () => api.get('/accounting/cost-centers');
export const createCostCenter = (data) => api.post('/accounting/cost-centers', data);
export const deleteCostCenter = (id) => api.delete(`/accounting/cost-centers/${id}`);
export const getCurrencies = () => api.get('/accounting/currencies');
export const createCurrency = (data) => api.post('/accounting/currencies', data);
export const getExchangeRates = () => api.get('/accounting/exchange-rates');
export const createExchangeRate = (data) => api.post('/accounting/exchange-rates', data);
export const getVendors = () => api.get('/accounting/vendors');
export const createVendor = (data) => api.post('/accounting/vendors', data);
export const updateVendor = (id, data) => api.put(`/accounting/vendors/${id}`, data);
export const deleteVendor = (id) => api.delete(`/accounting/vendors/${id}`);
export const getBills = () => api.get('/accounting/bills');
export const createBill = (data) => api.post('/accounting/bills', data);
export const updateBillStatus = (id, status) =>
  api.patch(`/accounting/bills/${id}/status`, typeof status === 'object' ? status : { status });
export const getPurchaseOrders = () => api.get('/accounting/purchase-orders');
export const createPurchaseOrder = (data) => api.post('/accounting/purchase-orders', data);
export const updatePurchaseOrder = (id, data) => api.put(`/accounting/purchase-orders/${id}`, data);
export const getARDashboard = () => api.get('/accounting/accounts-receivable/dashboard');
export const getBankAccounts = () => api.get('/accounting/bank-accounts');
export const createBankAccount = (data) => api.post('/accounting/bank-accounts', data);
export const getBankTransactions = (params) => api.get('/accounting/bank-transactions', { params });
export const createBankTransaction = (data) => api.post('/accounting/bank-transactions', data);
export const reconcileTransaction = (id, is_reconciled) =>
  api.patch(
    `/accounting/bank-transactions/${id}/reconcile`,
    typeof is_reconciled === 'object' ? is_reconciled : { is_reconciled }
  );
export const getCashFlowSummary = () => api.get('/accounting/cash-flow');
export const getFixedAssets = () => api.get('/accounting/fixed-assets');
export const createFixedAsset = (data) => api.post('/accounting/fixed-assets', data);
export const updateFixedAsset = (id, data) => api.put(`/accounting/fixed-assets/${id}`, data);
export const deleteFixedAsset = (id) => api.delete(`/accounting/fixed-assets/${id}`);
export const getBudgets = () => api.get('/accounting/budgets');
export const createBudget = (data) => api.post('/accounting/budgets', data);
export const deleteBudget = (id) => api.delete(`/accounting/budgets/${id}`);
export const getTaxRates = () => api.get('/accounting/tax-rates');
export const createTaxRate = (data) => api.post('/accounting/tax-rates', data);
export const updateTaxRate = (id, data) => api.put(`/accounting/tax-rates/${id}`, data);
export const getFinancialReports = () => api.get('/accounting/reports/financial');
export const getAgingReport = () => api.get('/accounting/reports/aging');
export const getAccountingSettings = () => api.get('/accounting/settings');
export const updateAccountingSettings = (data) => api.put('/accounting/settings', data);
export const getAccountingUsers = () => api.get('/accounting/users');
export const getAccountingRoles = () => api.get('/accounting/roles');
export const createAccountingRole = (data) => api.post('/accounting/roles', data);
export const getAccountingPermissions = () => api.get('/accounting/permissions');
export const seedAccountingPermissions = () => api.post('/accounting/permissions/seed');
export const getAuditLogs = () => api.get('/accounting/audit-logs');
export const getAccountingLoginHistory = () => api.get('/accounting/login-history');

// ==================== UTILITY FUNCTIONS ====================
export const getDeviceTypes = () => api.get('/device-types');
export const getDeviceBrands = () => api.get('/device-brands');
export const createDeviceBrand = (data) => api.post('/device-brands', data);
export const getRepairCategories = () => api.get('/repair-categories');
export const getTechnicians = () => api.get('/technicians');
export const getSuppliers = () => api.get('/suppliers');

// ==================== SALES API ====================
export const getSales = () => api.get('/sales');
export const getSaleById = (id) => api.get(`/sales/${id}`);
export const createSale = (saleData) => api.post('/sales', saleData);
export const updateSale = (id, saleData) => api.put(`/sales/${id}`, saleData);
export const deleteSale = (id) => api.delete(`/sales/${id}`);
export const updateSaleStatus = (id, statusData) => api.put(`/sales/${id}/status`, statusData);
export const getSalesStats = () => api.get('/sales-stats');
export const getRecentSales = () => api.get('/sales/recent');
export const getSalesByDateRange = (startDate, endDate) => 
  api.get(`/sales/date-range?start=${startDate}&end=${endDate}`);

// ==================== QUOTATIONS API ====================
export const getQuotations = () => api.get('/quotations');
export const getQuotationById = (id) => api.get(`/quotations/${id}`);
export const createQuotation = (quotationData) => api.post('/quotations', quotationData);
export const updateQuotation = (id, quotationData) => api.put(`/quotations/${id}`, quotationData);
export const deleteQuotation = (id) => api.delete(`/quotations/${id}`);
export const updateQuotationStatus = (id, status) => api.put(`/quotations/${id}/status`, { status });
export const convertToInvoice = (id) => api.post(`/quotations/${id}/convert-to-invoice`);
export const convertToSalesOrder = (id) => api.post(`/quotations/${id}/convert-to-sales-order`);
export const getQuotationStats = () => api.get('/quotation-stats');
export const sendQuotation = (id) => api.put(`/quotations/${id}/send`);
export const printQuotation = (id) => api.get(`/quotations/${id}/print`);

// ==================== USERS API ====================
export const getUsers = () => api.get('/users');
export const getUserById = (id) => api.get(`/users/${id}`);
export const createUser = (userData) => api.post('/users', userData);
export const updateUser = (id, userData) => api.put(`/users/${id}`, userData);
export const deleteUser = (id) => api.delete(`/users/${id}`);
export const toggleUserStatus = (id) => api.put(`/users/${id}/toggle-status`);
export const getUserStats = () => api.get('/users/stats');
export const updateUserProfile = (id, profileData) => api.put(`/users/${id}/profile`, profileData);
export const changePassword = (id, passwordData) => api.put(`/users/${id}/password`, passwordData);

// ==================== AUTHENTICATION API ====================
export const loginUser = (credentials) => api.post('/auth/login', credentials);
export const logoutUser = () => api.post('/auth/logout');
export const getProfile = () => api.get('/auth/profile');
export const registerUser = (userData) => api.post('/auth/register', userData);
export const verifyToken = () => api.get('/auth/verify');
export const resetPassword = (email) => api.post('/auth/reset-password', { email });
export const changeUserPassword = (data) => api.post('/auth/change-password', data);

// Login History (Super Admin)
export const getLoginHistory = (params = {}) => api.get('/login-history', { params });
export const getLoginHistoryAiReport = () => api.get('/login-history/ai-report');
export const markLoginHistoryReviewed = (id) => api.post(`/login-history/${id}/review`);
export const trackLoginActivity = (data) => api.post('/auth/activity', data);

// ==================== REPORTS API ====================
export const getSalesReport = (params) => api.get('/reports/sales', { params });
export const getInventoryReport = () => api.get('/reports/inventory');
export const getCustomerReport = () => api.get('/reports/customers');
export const getFinancialReport = (params) => api.get('/reports/financial', { params });
export const getTaxReport = (params) => api.get('/reports/tax', { params });
export const getBranchPerformance = (params) => api.get('/reports/branch-performance', { params });
export const getSalesTargets = (params) => api.get('/reports/sales-targets', { params });
export const saveSalesTarget = (data) => api.post('/reports/sales-targets', data);
export const deleteSalesTarget = (id) => api.delete(`/reports/sales-targets/${id}`);

export const exportReportToCSV = (reportType, params = {}) => {
  return api.get(`/reports/export/${reportType}`, {
    params,
    responseType: 'blob'
  });
};

export const exportReportToPDF = (reportType, params = {}) => {
  return api.get(`/reports/export/${reportType}/pdf`, {
    params,
    responseType: 'blob'
  });
};

export const exportReportToExcel = (reportType, params = {}) => {
  return api.get(`/reports/export/${reportType}`, {
    params: { ...params, format: 'excel' },
    responseType: 'blob'
  });
};

// ==================== INVENTORY API ====================
export const getInventory = () => api.get('/inventory');
export const getInventoryById = (id) => api.get(`/inventory/${id}`);
export const updateInventory = (id, inventoryData) => api.put(`/inventory/${id}`, inventoryData);
export const getInventoryTransactions = () => api.get('/inventory/transactions');
export const getStockAlerts = () => api.get('/inventory/alerts');
export const adjustStock = (id, adjustmentData) => api.post(`/inventory/${id}/adjust`, adjustmentData);
export const transferStock = (transferData) => api.post('/inventory/transfer', transferData);

// ==================== CREDIT API ====================
export const recordCreditSale = (data) => api.post('/pos', {
  ...data,
  payment_method: 'credit',
  amount_paid: 0,
  change_amount: 0
});

export const recordPayment = (data) => api.post('/credit/payment', data);
export const getCreditDashboard = () => api.get('/credit/dashboard');
export const getOverdueAccounts = () => api.get('/credit/overdue');
export const generateStatement = (customerId, params) => api.get(`/credit/statements/${customerId}`, { params });
export const getCreditTransactions = (customerId, params) => api.get(`/credit/transactions/${customerId}`, { params });
export const updateCreditLimit = (customerId, data) => api.put(`/customers/${customerId}`, data);
export const testCreditAPI = () => api.get('/credit/test');

// ==================== CATEGORIES API ====================
export const getCategories = () => api.get('/categories');
export const createCategory = (categoryData) => api.post('/categories', categoryData);
export const updateCategory = (id, categoryData) => api.put(`/categories/${id}`, categoryData);
export const deleteCategory = (id) => api.delete(`/categories/${id}`);

// ==================== SETTINGS API ====================
export const getSettings = () => api.get('/settings');
export const updateSettings = (settingsData) => api.put('/settings', settingsData);
export const getTaxSettings = () => api.get('/settings/tax');
export const updateTaxSettings = (taxData) => api.put('/settings/tax', taxData);
export const getCompanyInfo = () => api.get('/settings/company');
export const updateCompanyInfo = (companyData) => api.put('/settings/company', companyData);

// ==================== FILE UPLOAD API ====================
export const uploadFile = (file, type = 'product') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);
  
  return api.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const uploadProductImage = (productId, file) => {
  const formData = new FormData();
  formData.append('image', file);
  
  return api.post(`/products/${productId}/upload-image`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// ==================== SCHOOL PROJECTS API ====================

// Dashboard
export const getSchoolProjectStats = () => api.get('/school-projects/dashboard');

// Projects
export const getSchoolProjects = (params) => api.get('/school-projects/projects', { params });
export const getSchoolProjectById = (id) => api.get(`/school-projects/projects/${id}`);
export const createSchoolProject = (projectData) => api.post('/school-projects/projects', projectData);
export const updateSchoolProject = (id, projectData) => api.put(`/school-projects/projects/${id}`, projectData);
export const deleteSchoolProject = (id) => api.delete(`/school-projects/projects/${id}`);

// Schools
export const getSchools = () => api.get('/school-projects/schools');
export const getSchoolById = (id) => api.get(`/school-projects/schools/${id}`);
export const createSchool = (schoolData) => api.post('/school-projects/schools', schoolData);
export const updateSchool = (id, schoolData) => api.put(`/school-projects/schools/${id}`, schoolData);
export const deleteSchool = (id) => api.delete(`/school-projects/schools/${id}`);

// Computer Parts Management
export const getComputerParts = () => api.get('/school-projects/inventory/parts');
export const getComputerPartById = (id) => api.get(`/school-projects/inventory/parts/${id}`);
export const addComputerPart = (partData) => api.post('/school-projects/inventory/parts', partData);
export const updateComputerPart = (id, partData) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('📤 Updating computer part:', id, partData);
  }
  return api.put(`/school-projects/inventory/parts/${id}`, partData);
};
export const deleteComputerPart = (id) => api.delete(`/school-projects/inventory/parts/${id}`);
export const searchComputerParts = (query) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('📤 Searching computer parts with query:', query);
  }
  return api.get(`/school-projects/inventory/search?q=${encodeURIComponent(query)}`);
};
export const getLowStockParts = () => api.get('/school-projects/inventory/low-stock');
export const updatePartStock = (id, data) => api.put(`/school-projects/inventory/parts/${id}/stock`, data);
export const getPartCategories = () => api.get('/school-projects/inventory/categories');
export const getPartStats = () => api.get('/school-projects/inventory/stats');

// Inventory Management
export const getSchoolProjectInventory = () => api.get('/school-projects/inventory');
export const getInventoryItemById = (id) => api.get(`/school-projects/inventory/${id}`);
export const updateInventoryItem = (id, inventoryData) => api.put(`/school-projects/inventory/${id}`, inventoryData);
export const getStockAnalysis = () => api.get('/school-projects/inventory/analysis');

// Technicians Management
export const getTechniciansList = () => api.get('/school-projects/technicians');
export const createTechnician = (technicianData) => api.post('/school-projects/technicians', technicianData);
export const getTechnicianById = (id) => api.get(`/school-projects/technicians/${id}`);
export const updateTechnician = (id, technicianData) => api.put(`/school-projects/technicians/${id}`, technicianData);
export const deleteTechnician = (id) => api.delete(`/school-projects/technicians/${id}`);
export const getTechnicianStats = () => api.get('/school-projects/technicians/stats');

// Technician Parts Assignment
export const issuePartsToTechnician = (data) => api.post('/school-projects/technician-parts/issue', data);
export const returnPartsFromTechnician = (data) => api.post('/school-projects/technician-parts/return', data);
export const bulkIssueParts = (data) => api.post('/school-projects/technician-parts/bulk-issue', data);
export const getTechnicianAssignments = (params = {}) => {
  const query = Object.keys(params).length > 0 ? `?${new URLSearchParams(params)}` : '';
  return api.get(`/school-projects/technician-parts/assignments${query}`);
};
export const getAssignmentById = (id) => api.get(`/school-projects/technician-parts/assignments/${id}`);
export const getTechnicianAssignmentHistory = (technicianId) => api.get(`/school-projects/technicians/${technicianId}/assignments`);
export const updateAssignment = (id, data) => api.put(`/school-projects/technician-parts/assignments/${id}`, data);

// Purchases
export const getPurchases = () => api.get('/school-projects/purchases');
export const getPurchaseById = (id) => api.get(`/school-projects/purchases/${id}`);
export const recordPurchase = (purchaseData) => api.post('/school-projects/purchases', purchaseData);
export const updatePurchase = (id, purchaseData) => api.put(`/school-projects/purchases/${id}`, purchaseData);
export const deletePurchase = (id) => api.delete(`/school-projects/purchases/${id}`);

// Reports
export const getSchoolProjectReports = (params) => api.get('/school-projects/reports', { params });
export const getProjectStatusReport = () => api.get('/school-projects/reports/project-status');
export const getInventoryReportSchool = () => api.get('/school-projects/reports/inventory');
export const getFinancialReportSchool = (params) => api.get('/school-projects/reports/financial', { params });

// Computer Management
export const addComputerToProject = (projectId, data) => api.post(`/school-projects/projects/${projectId}/computers`, data);
export const assignPartsToComputer = (computerId, data) => api.post(`/school-projects/computers/${computerId}/assign-parts`, data);
export const updateComputerStatus = (computerId, data) => api.put(`/school-projects/computers/${computerId}`, data);
export const getProjectComputers = (projectId) => api.get(`/school-projects/projects/${projectId}/computers`);
export const getComputerDetails = (computerId) => api.get(`/school-projects/computers/${computerId}`);

// Settings
export const getSchoolProjectSettings = () => api.get('/school-projects/settings');
export const updateSchoolProjectSettings = (settingsData) => api.put('/school-projects/settings', settingsData);

// ==================== UTILITY FUNCTIONS ====================
export const buildQueryString = (params) => {
  if (!params || Object.keys(params).length === 0) return '';
  
  const query = Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
  
  return query ? `?${query}` : '';
};

export const checkAPIHealth = async () => {
  try {
    const response = await api.get('/test');
    return {
      healthy: true,
      message: 'API is working',
      data: response
    };
  } catch (error) {
    return {
      healthy: false,
      message: error.message || 'API connection failed',
      error
    };
  }
};

export const batchUpdateProducts = (productsData) => api.put('/products/batch', productsData);
export const batchDeleteProducts = (ids) => api.delete('/products/batch', { data: { ids } });

export const posAPI = {
  getStats: getPOSStats,
  create: createPOS,
  getList: getPOS,
  getById: getPOSById,
  refund: refundPOS,
  void: voidPOS,
  searchSerials: searchSerialNumbers,
  getWarranty: getWarrantyInfo,
  updateStatus: updatePOSStatus
};

export const createCreditPOS = (data) => {
  return createPOS({
    ...data,
    payment_method: 'credit',
    amount_paid: 0,
    change_amount: 0
  });
};

// ==================== DEFAULT EXPORT ====================
// Create a default export object with all methods
const apiService = {
  // Test & Debug
  testConnection,
  testPOSConnection,
  debugAPI,
  checkAPIHealth,
  
  // Dashboard
  getDashboardStats,
  
  // POS
  ...posAPI,
  createCreditPOS,
  updatePOSStatus,
  
  // Products
  getProducts,
  getOfficeIssues,
  getOfficeIssueById,
  createOfficeIssue,
  getProductById,
  createProduct,
  updateProduct,
  deleteProduct,
  getProductByBarcode,
  searchProducts,
  getLowStockProducts,
  getProductCategories,
  getProductStats,
  bulkUpdateProducts,
  updateProductStock,
  toggleProductStatus,

  // GRN
  getGRNList,
  getGRNById,
  createGRN,
  updatePendingGRN,
  approveGRN,
  getGRNByProduct,
  getGRNBySerial,
  getGRNSuppliers,
  
  // Product Serials
  getProductSerials,
  getAllProductSerials,
  addProductSerial,
  updateProductSerial,
  deleteProductSerial,
  updateSerialStatus,
  reserveSerials,
  searchSerials,
  
  // Product Issues
  getAllProductIssues,
  getProductIssues,
  getProductIssueById,
  createProductIssue,
  updateProductIssue,
  updateProductIssueStatus,
  deleteProductIssue,
  resolveProductIssue,
  getProductIssuesStats,
  exportProductIssuesToCSV,
  exportProductIssuesToPDF,
  exportProductSummary,
  exportIssuesReport,
  
  // Customers
  getCustomers,
  getCustomerById,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  searchCustomers,
  getCustomerStats,
  getActiveCustomers,
  getCreditCustomers,
  
  // Invoices
  getInvoices,
  getInvoiceById,
  createInvoice,
  updateInvoice,
  deleteInvoice,
  updateInvoiceStatus,
  markAsPaid,
  getInvoiceStats,
  getOverdueInvoices,
  sendInvoice,
  printInvoice,
  downloadInvoice,
  sendInvoiceEmail,
  
  // Repair Invoices
  getRepairs,
  getRepairById,
  createRepair,
  updateRepair,
  deleteRepair,
  updateDiagnosis,
  createRepairQuotation,
  updateRepairQuotation,
  acceptQuotation,
  rejectQuotation,
  addRepairParts,
  completeRepair,
  recordRepairPayment,
  deliverRepair,
  getRepairStats,
  searchRepairsBySerial,
  getWarrantyRepairs,
  getRepairCustomers,
  getRepairCustomerProfile,

  getRentals,
  getRentalById,
  createRental,
  getRentalStats,
  returnRental,
  cancelRental,
  extendRental,
  getRentalPendingInvoices,
  payRentalInvoice,
  trackRentalSerial,
  getRentalCustomers,
  getRentalCustomerProfile,
  getRentalInvoices,
  getRentalInvoiceById,
  getRentalAgreementTemplate,
  uploadRentalAgreementTemplate,
  uploadRentalAgreement,
  getRentalDashboard,
  getRentalReport,
  getOutsideRentals,
  getOutsideRentalProfile,
  
  // Sales
  getSales,
  getSaleById,
  createSale,
  updateSale,
  deleteSale,
  updateSaleStatus,
  getSalesStats,
  getRecentSales,
  getSalesByDateRange,
  
  // Quotations
  getQuotations,
  getQuotationById,
  createQuotation,
  updateQuotation,
  deleteQuotation,
  updateQuotationStatus,
  convertToInvoice,
  convertToSalesOrder,
  getQuotationStats,
  sendQuotation,
  printQuotation,
  
  // Users
  getUsers,
  getUserById,
  createUser,
  updateUser,
  deleteUser,
  toggleUserStatus,
  getUserStats,
  updateUserProfile,
  changePassword,
  
  // Auth
  loginUser,
  logoutUser,
  getProfile,
  registerUser,
  verifyToken,
  resetPassword,
  changeUserPassword,
  getLoginHistory,
  getLoginHistoryAiReport,
  markLoginHistoryReviewed,
  trackLoginActivity,
  
  // Reports
  getSalesReport,
  getInventoryReport,
  getCustomerReport,
  getFinancialReport,
  getTaxReport,
  exportReportToCSV,
  exportReportToPDF,
  getBranchPerformance,
  getSalesTargets,
  saveSalesTarget,
  deleteSalesTarget,
  
  // Inventory
  getInventory,
  getInventoryById,
  updateInventory,
  getInventoryTransactions,
  getStockAlerts,
  adjustStock,
  transferStock,
  
  // Credit Management
  recordCreditSale,
  recordPayment,
  getCreditDashboard,
  getOverdueAccounts,
  generateStatement,
  getCreditTransactions,
  updateCreditLimit,
  testCreditAPI,
  
  // Categories
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  
  // Settings
  getSettings,
  updateSettings,
  getTaxSettings,
  updateTaxSettings,
  getCompanyInfo,
  updateCompanyInfo,
  
  // File Upload
  uploadFile,
  uploadProductImage,
  
  // Utilities
  getDeviceTypes,
  getDeviceBrands,
  createDeviceBrand,
  getRepairCategories,
  getTechnicians,
  getSuppliers,
  buildQueryString,
  
  // School Projects
  getSchoolProjectStats,
  getSchoolProjects,
  getSchoolProjectById,
  createSchoolProject,
  updateSchoolProject,
  deleteSchoolProject,
  getSchools,
  getSchoolById,
  createSchool,
  updateSchool,
  deleteSchool,
  getComputerParts,
  getComputerPartById,
  addComputerPart,
  updateComputerPart,
  deleteComputerPart,
  searchComputerParts,
  getLowStockParts,
  updatePartStock,
  getPartCategories,
  getPartStats,
  getSchoolProjectInventory,
  getInventoryItemById,
  updateInventoryItem,
  getStockAnalysis,
  getTechniciansList,
  createTechnician,
  getTechnicianById,
  updateTechnician,
  deleteTechnician,
  getTechnicianStats,
  issuePartsToTechnician,
  returnPartsFromTechnician,
  bulkIssueParts,
  getTechnicianAssignments,
  getAssignmentById,
  getTechnicianAssignmentHistory,
  updateAssignment,
  getPurchases,
  getPurchaseById,
  recordPurchase,
  updatePurchase,
  deletePurchase,
  getSchoolProjectReports,
  getProjectStatusReport,
  getInventoryReportSchool,
  getFinancialReportSchool,
  addComputerToProject,
  assignPartsToComputer,
  updateComputerStatus,
  getProjectComputers,
  getComputerDetails,
  getSchoolProjectSettings,
  updateSchoolProjectSettings,

  // Branches
  getBranches,
  getActiveBranches,
  createBranch,
  updateBranch,
  deleteBranch,
  getBranchUsers,
  createBranchManager,
  createBranchStaff,
  shiftUserBranch,
  resetBranchUserPassword,

  // Advanced repairs
  getWorkshopQueue,
  assignTechnician,
  createServiceRequest,
  getServiceRequest,
  updateRepairStatus,
  updateRepairType,
  updateWorkflowStep,
  addRepairNote,
  addRepairAttachment,
  createPartRequest,
  getPartRequests,
  approvePartRequest,
  rejectPartRequest,
  deletePartRequest,
  generateRepairInvoice,
  getRepairInvoice,
  getRepairInvoices,
  updateRepairInvoice,
  getRepairManagementReport,
  createRepairReportExpense,
  deleteRepairReportExpense,
  getOutsourceRepairs,
  getOutsourceTechnicianDashboard,
  updateOutsourcePayment,
  getRepairQuotations,
  getRepairQuotation,
  setActiveRepairQuotation,
  updateRepairQuotation,
  rejectQuotation,
  getOutsourceTechnicians,
  createOutsourceTechnician,
  updateOutsourceTechnician,
  deleteOutsourceTechnician,
  getRepairCommonFaults,
  createRepairCommonFault,
  updateRepairCommonFault,
  deleteRepairCommonFault,
  getInhouseTechnicians,
  createInhouseTechnician,
  updateInhouseTechnician,
  deleteInhouseTechnician,
  submitRepairDiagnosis,
  saveRepairOtherPayments,
  approveRepairDiagnosis,
  rejectRepairDiagnosis,
  getDiagnosisParts,
  markPartsReceived,
  completeRepairCollection,
  markRepairCollected,
  getReadyToCollect,
  getRepairDoneList,
  updateOutsourceWorkflow,
  setOutsourceRepairPrice,
  assignOutsourceTechnician,
  informOutsourceTechOngoing,
  informOutsourcePartialFix,
  markOutsourceNeedCollect,
  markOutsourceReceivedQa,
  rejectRepairQuotation,
  getQaQueue,
  submitQaCheck,
  sendRepairSms,
  shiftRepairBranch,
  getSsiTechnicalMeta,
  getSsiTechnicalJobs,
  getSsiTechnicalJob,
  createSsiTechnicalItem,
  createSsiBulkGrn,
  saveSsiTechnicalInspection,
  addSsiPartRequest,
  approveSsiTechnicalStock,
  updateSsiStockProductName,
  rejectSsiTechnicalJob,
  deleteSsiTechnicalJob,

  // Accounting
  getAccountingDashboard,
  getChartOfAccounts,
  createChartOfAccount,
  updateChartOfAccount,
  deleteChartOfAccount,
  getJournalEntries,
  createJournalEntry,
  updateJournalEntryStatus,
  getAccountingPeriods,
  createAccountingPeriod,
  updatePeriodStatus,
  deleteAccountingPeriod,
  getCostCenters,
  createCostCenter,
  deleteCostCenter,
  getCurrencies,
  createCurrency,
  getExchangeRates,
  createExchangeRate,
  getVendors,
  createVendor,
  updateVendor,
  deleteVendor,
  getBills,
  createBill,
  updateBillStatus,
  getPurchaseOrders,
  createPurchaseOrder,
  updatePurchaseOrder,
  getARDashboard,
  getBankAccounts,
  createBankAccount,
  getBankTransactions,
  createBankTransaction,
  reconcileTransaction,
  getCashFlowSummary,
  getFixedAssets,
  createFixedAsset,
  updateFixedAsset,
  deleteFixedAsset,
  getBudgets,
  createBudget,
  deleteBudget,
  getTaxRates,
  createTaxRate,
  updateTaxRate,
  getFinancialReports,
  getAgingReport,
  getAccountingSettings,
  updateAccountingSettings,
  getAccountingUsers,
  getAccountingRoles,
  createAccountingRole,
  getAccountingPermissions,
  seedAccountingPermissions,
  getAuditLogs,
  getAccountingLoginHistory,
  exportReportToExcel,

  // Axios verbs (RepairView etc. call api.get/post/put directly)
  get: (...args) => api.get(...args),
  post: (...args) => api.post(...args),
  put: (...args) => api.put(...args),
  patch: (...args) => api.patch(...args),
  delete: (...args) => api.delete(...args),

  // Raw axios instance
  api
};

export default apiService;